import { useEffect, useRef, useState } from 'react';
import { AVATAR_GROUPS, isOffensive } from '@brainfart/shared';
import { Sheet } from './Sheet';
import { getIdentity, saveIdentity, useIdentity } from '../identity';
import { track } from '../telemetry';

// "Your player card": name + avatar, picked once and used everywhere.
export function ProfileSheet({ open, onClose, onSaved, title = 'Your player card', cta = 'Save' }: {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
  title?: string;
  cta?: string;
}) {
  const [name, setName] = useState('');
  const [avatar, setAvatar] = useState('');
  const [groupKey, setGroupKey] = useState<string>(AVATAR_GROUPS[0].key);
  const inputRef = useRef<HTMLInputElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const group = AVATAR_GROUPS.find(g => g.key === groupKey) ?? AVATAR_GROUPS[0];

  useEffect(() => {
    if (!open) return;
    const me = getIdentity();
    setName(me.name);
    setAvatar(me.avatar);
    // Open on the tab that has your current avatar.
    setGroupKey((AVATAR_GROUPS.find(g => (g.avatars as readonly string[]).includes(me.avatar)) ?? AVATAR_GROUPS[0]).key);
  }, [open]);

  const blocked = isOffensive(name);
  const valid = name.trim().length > 0 && !blocked;

  const save = () => {
    if (!valid) {
      inputRef.current?.focus();
      return;
    }
    saveIdentity(name.trim(), avatar);
    track({ type: 'profile_saved', payload: { avatar } });
    // Close first, so onSaved can open the next step (onboarding) cleanly.
    onClose();
    onSaved?.();
  };

  return (
    <Sheet open={open} onClose={onClose} label={title}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '3.5rem', lineHeight: 1, marginBottom: '0.5rem' }}>{avatar}</div>
        <h2 style={{ fontSize: '1.3rem', fontWeight: 800, color: '#FFC93C', marginBottom: '1rem' }}>{title}</h2>
        <input
          ref={inputRef}
          value={name}
          onChange={e => setName(e.target.value.slice(0, 20))}
          onKeyDown={e => { if (e.key === 'Enter') save(); }}
          placeholder="Your name"
          maxLength={20}
          enterKeyHint="done"
          autoComplete="nickname"
          style={{
            width: '100%', padding: '0.85rem 1rem', borderRadius: '12px', border: '1px solid #2E2B45', background: '#100E22',
            color: '#fff', fontSize: '1.05rem', outline: 'none', textAlign: 'center', marginBottom: blocked ? '0.35rem' : '1rem',
          }}
        />
        {blocked && <p style={{ color: '#FF6B6B', fontSize: '0.8rem', marginBottom: '0.75rem' }}>That name won't be shown to other players. Try another?</p>}
        <p style={{ color: '#888', fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.08em', marginBottom: '0.5rem' }}>PICK YOUR AVATAR</p>
        <div role="tablist" aria-label="Avatar groups" style={{ display: 'flex', gap: '0.35rem', marginBottom: '0.5rem', overflowX: 'auto', scrollbarWidth: 'none' }}>
          {AVATAR_GROUPS.map(g => (
            <button
              key={g.key}
              role="tab"
              aria-selected={g.key === group.key}
              onClick={() => { setGroupKey(g.key); gridRef.current?.scrollTo({ top: 0 }); }}
              style={{
                flex: '1 0 auto', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem',
                padding: '0.4rem 0.6rem', minHeight: 36, borderRadius: '999px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 700,
                color: g.key === group.key ? '#100E22' : '#bbb',
                background: g.key === group.key ? '#FFC93C' : '#100E22',
                border: `1px solid ${g.key === group.key ? '#FFC93C' : '#2E2B45'}`,
              }}
            >
              <span aria-hidden>{g.icon}</span>{g.label}
            </button>
          ))}
        </div>
        <div
          ref={gridRef}
          role="radiogroup"
          aria-label="Avatar"
          style={{
            display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '0.4rem', marginBottom: '1.25rem',
            // About four rows, then scroll, so the sheet stays short on phones.
            maxHeight: 216, overflowY: 'auto', overscrollBehavior: 'contain', padding: '2px',
          }}
        >
          {group.avatars.map(a => (
            <button
              key={a}
              role="radio"
              aria-checked={a === avatar}
              aria-label={`Avatar ${a}`}
              onClick={() => setAvatar(a)}
              style={{
                fontSize: '1.7rem', height: 48, borderRadius: '12px', cursor: 'pointer',
                background: a === avatar ? 'rgba(255,201,60,0.18)' : '#1B1830',
                border: `2px solid ${a === avatar ? '#FFC93C' : 'transparent'}`,
              }}
            >
              {a}
            </button>
          ))}
        </div>
        <button className="btn btn-primary" onClick={save} disabled={!valid} style={{ width: '100%', padding: '0.95rem' }}>{cta}</button>
      </div>
    </Sheet>
  );
}

// Compact "🐼 Prasad ✎" chip that opens the player card.
export function ProfileChip({ style }: { style?: React.CSSProperties }) {
  const me = useIdentity();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        aria-label="Edit your player card"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.35rem 0.75rem 0.35rem 0.45rem', minHeight: 40,
          borderRadius: '999px', border: '1px solid #2E2B45', background: '#1B1830', color: '#ddd', fontSize: '0.9rem', cursor: 'pointer', ...style,
        }}
      >
        <span style={{ fontSize: '1.3rem', lineHeight: 1 }}>{me.avatar}</span>
        <span style={{ maxWidth: '8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{me.name || 'Set up profile'}</span>
        <span aria-hidden style={{ color: '#888', fontSize: '0.8rem' }}>✎</span>
      </button>
      <ProfileSheet open={open} onClose={() => setOpen(false)} />
    </>
  );
}

// "Playing as 🐼 Prasad · Edit" where a name used to be typed; a first-timer
// gets the name box inline instead.
export function PlayingAs({ onNameChange }: { onNameChange?: (name: string) => void }) {
  const me = useIdentity();
  const [draft, setDraft] = useState('');
  if (me.name) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', padding: '0.6rem 0.8rem', borderRadius: '14px', background: '#1B1830', border: '1px solid #2E2B45', marginBottom: '1.25rem' }}>
        <span style={{ color: '#aaa', fontSize: '0.85rem' }}>Playing as</span>
        <ProfileChip />
      </div>
    );
  }
  return (
    <div style={{ marginBottom: '1.25rem' }}>
      <label style={{ display: 'block', color: '#aaa', fontSize: '0.85rem', marginBottom: '0.35rem', textAlign: 'left' }}>Your name</label>
      <input
        value={draft}
        onChange={e => { setDraft(e.target.value.slice(0, 20)); onNameChange?.(e.target.value.slice(0, 20)); }}
        onBlur={() => { if (draft.trim() && !isOffensive(draft)) saveIdentity(draft.trim(), getIdentity().avatar); }}
        placeholder="What should opponents call you?"
        maxLength={20}
        autoComplete="nickname"
        style={{ width: '100%', padding: '0.8rem 1rem', borderRadius: '12px', border: '1px solid #2E2B45', background: '#100E22', color: '#fff', fontSize: '1rem', outline: 'none' }}
      />
      {isOffensive(draft) && <p style={{ color: '#FF6B6B', fontSize: '0.8rem', marginTop: '0.35rem', textAlign: 'left' }}>That name won't be shown to other players. Try another?</p>}
    </div>
  );
}
